import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import { adminCourseStepSectionDeleteAction, adminCourseStepSectionsListAction } from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function CourseStepSections() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminCourseStepSectionsListAction({ page, per_page: perPage, q });
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.message || "Failed to load step sections.");
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load step sections.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, reloadNonce]);

  const viewRows = useMemo(() => {
    return rows.map((r) => ({
      raw: r,
      id: r?.id,
      title: r?.title || "—",
      displayOrder: r?.display_order != null && r?.display_order !== "" ? String(r.display_order) : "0",
      statusLabel: Number(r?.is_active) === 1 ? "Active" : "Inactive",
      created: formatDate(r?.created_on)
    }));
  }, [rows]);

  const total = meta?.total ? Number(meta.total) : viewRows.length;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && viewRows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && viewRows.length ? start + viewRows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminCourseStepSectionDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success("Step section deleted", { toastId: "css-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.message || "Delete failed", { toastId: "css-delete" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Step sections unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="breadcrumb">
        <span>Step sections</span>
      </div>

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by title..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>

        <div className="flex items-center gap-3">
          <Link to="new" className="btn btn-primary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add step section
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Display order</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : (
                viewRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.title}</strong>
                    </td>
                    <td>{r.displayOrder}</td>
                    <td>
                      <span className={`badge ${r.statusLabel === "Active" ? "badge-success" : "badge-gray"}`}>
                        {r.statusLabel}
                      </span>
                    </td>
                    <td>{r.created}</td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`${r.id}/edit`} className="action-link">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => openDelete(r.raw)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                        <Link
                          to={`${r.id}/questions`}
                          className="action-link"
                          title="Questions"
                          style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
                          </svg>
                          Questions
                        </Link>
                    
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && viewRows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No step sections found.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            {total === 0
              ? "No sections"
              : `Showing ${start}–${end} of ${total} Section${total === 1 ? "" : "s"} · Page ${page} of ${totalPages}`}
          </span>
          {total > 0 ? (
            <div className="pagination-pages">
              <button type="button" title="First page" onClick={() => setPage(1)} disabled={page <= 1 || loading}>{"<<"}
              </button>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                ‹
              </button>
              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={p === page ? "active" : ""}
                  type="button"
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                ›
              </button>
              <button
                type="button"
                title="Last page"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
              >{">>"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="step section"
        itemName={deleteRow?.title || "Step section"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
